// src/components/weeklyReport/ui.js
//
// Shared pieces for the patient-level weekly report: the pop-up shell, button
// styles and status chips. Every class here has its own light-mode rule in
// index.css (weekly-report-*), so nothing comes out white-on-white.

import { useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { modalBackdrop, modalPanel } from "../motionPresets";
import { REPORT_STATUSES } from "../../services/weeklyReportModel";

export const BTN_PRIMARY =
  "weekly-report-solid-btn inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 text-sm font-semibold text-white ring-1 ring-white/20 hover:shadow-[0_10px_30px_-5px_rgba(139,92,246,0.6)] active:scale-[0.98] transition disabled:opacity-50 disabled:cursor-not-allowed";
export const BTN_SOFT =
  "weekly-report-soft-btn inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-sm font-medium text-slate-200 transition disabled:opacity-50 disabled:cursor-not-allowed";
export const BTN_SMALL =
  "weekly-report-soft-btn inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-[11px] font-semibold text-slate-200 transition disabled:opacity-50";
export const INPUT =
  "w-full px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-purple-500/20 transition [color-scheme:dark]";
export const LABEL = "block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1";

/** Coloured chip for a patient's status. */
export const StatusChip = ({ status, children }) => {
  const s = REPORT_STATUSES.find((x) => x.id === status);
  return (
    <span
      className={`weekly-report-chip weekly-report-chip--${status || "none"} inline-flex items-center px-2 py-0.5 rounded-md border text-[10px] font-semibold`}
    >
      {children ?? (s ? s.label : "No status")}
    </span>
  );
};

/**
 * Pop-up shell. Closing it (X, Escape, clicking outside) never loses work:
 * everything typed lives in the autosaved draft.
 */
export const WrModal = ({ open, onClose, title, subtitle, icon: Icon, children, footer, wide = false }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Drawn into <body>, not where it's used: a card with a glass blur
  // (backdrop-filter) or a transform traps "position: fixed" inside its own
  // box, which squeezed the pop-up into the card and cut it off (Admin
  // Reports → Submitted reports, 2026-10-09).
  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          {...modalBackdrop}
          className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 font-[Manrope]"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) onClose?.();
          }}
        >
          <motion.div
            {...modalPanel}
            role="dialog"
            aria-modal="true"
            aria-label={title}
            className={`weekly-report-modal relative w-full ${
              wide ? "max-w-2xl" : "max-w-lg"
            } max-h-[90vh] overflow-hidden rounded-2xl border border-white/10 bg-[#0c0826]/95 backdrop-blur-2xl shadow-[0_30px_80px_-20px_rgba(124,58,237,0.6)] text-slate-100 flex flex-col`}
          >
            <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-purple-400 via-fuchsia-400 to-blue-400 opacity-80 pointer-events-none" />
            <div className="relative px-5 py-3.5 border-b border-white/10 flex items-center gap-3 shrink-0">
              {Icon && (
                <div className="weekly-report-icon w-9 h-9 rounded-xl bg-purple-500/15 border border-white/15 flex items-center justify-center shrink-0">
                  <Icon className="w-4.5 h-4.5 text-purple-300" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h2 className="text-base font-semibold text-white leading-tight truncate">{title}</h2>
                {subtitle && <div className="text-[11px] text-slate-400 truncate">{subtitle}</div>}
              </div>
              <button
                type="button"
                onClick={onClose}
                className="weekly-report-soft-btn w-8 h-8 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/10 flex items-center justify-center text-slate-300 transition shrink-0"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="relative overflow-y-auto flex-1 p-5">{children}</div>
            {footer && (
              <div className="relative px-5 py-3 border-t border-white/10 flex flex-wrap items-center justify-end gap-2 shrink-0">
                {footer}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

/** "Sep 28 – 30, 2026"-style label for a saved report's period. */
export const periodText = (r) =>
  r?.periodStart && r?.periodEnd
    ? r.periodStart === r.periodEnd
      ? r.periodStart
      : `${r.periodStart} → ${r.periodEnd}`
    : r?.weekOf || "";
